import { z } from 'zod';
import { NEW_ROLE_FLAG, NO_ROLE_FLAG } from '#components/patterns/entity-select/role/option-constants.ts';
import { portSchemaAllowZeroRequired, portSchemaRequired } from '#schemas/general/port.ts';
import { makeHostNameSchema, makeOverlayIPv4Schema, makeOverlayIPv6Schema } from '#schemas/host/index.ts';
import { makePublicIPOrHostnameSchema } from '#schemas/lighthouse/index.ts';
import { roleNameSchema } from '#schemas/role/name.ts';
import { tagNameSchema } from '#schemas/tag/name.ts';
import type { HostKind } from '#util/host.ts';

export const makeHostGeneralFormPartSchema = (kind: HostKind, v4CIDRs: readonly string[], v6CIDRs: readonly string[]) =>
  z
    .object({
      name: makeHostNameSchema(kind),
      // Only the lighthouse form exposes this field; for other kinds it's whatever the api set in
      // staticAddresses[0], round-tripped unvalidated.
      publicIPOrHostname: kind === 'lighthouse' ? makePublicIPOrHostnameSchema(v4CIDRs) : z.string(),
      // Hosts may leave the port unset (0 = pick one at random); lighthouses and relays must be reachable.
      listenPort: kind === 'host' ? portSchemaAllowZeroRequired : portSchemaRequired,
      // This is determined automatically if there is only one network, otherwise the user chooses
      networkID: z.string().startsWith('network-', 'Please choose a network'),
      ipv4Address: makeOverlayIPv4Schema(v4CIDRs),
      ipv6Address: makeOverlayIPv6Schema(v6CIDRs),
      ipv4Enabled: z.boolean(),
      tags: z.array(tagNameSchema),
    })
    .and(
      z.union([
        z.object({
          roleID: z.literal(NEW_ROLE_FLAG),
          roleName: roleNameSchema,
        }),
        z.object({
          roleID: z.union([z.literal(NO_ROLE_FLAG), z.string().startsWith('role-', 'Please choose an option')]),
          roleName: z.string().optional(),
        }),
      ])
    );
